import { Injectable, Inject } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, map } from 'rxjs/operators';
import { GroupBase, InterpolatedBlueprint } from './models/blueprint.model';
import { MappedTemplateField, ResolvedTemplateField } from './models/template.model';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@Injectable()
export class InterpolationResolverService {
  /**
   * Iterate through the columns array ({@link InterpolatedBlueprint}) and
   * process each group using the {@link processColumnGroups} method.
   *
   * It then fork join all the received processed groups to later return one
   * observable the will get resolved into an {@link InterpolatedBlueprint}
   * of {@link ResolvedTemplateField}
   *
   * @see processColumnGroups
   * @see ResolvedTemplateField
   */
  constructor(@Inject(CbreLoggerToken) protected logger?: CbreLogger) {}
  resolve(
    interpolatedRecord: InterpolatedBlueprint<MappedTemplateField>
  ): Observable<InterpolatedBlueprint<ResolvedTemplateField>> {
    const columnsToProcess: Array<Observable<Array<GroupBase<ResolvedTemplateField>>>> = [];
    for (const group of interpolatedRecord) {
      const resolvedGroups$ = this.processColumnGroups(group);
      columnsToProcess.push(resolvedGroups$);
    }

    return forkJoin(columnsToProcess);
  }

  /**
   * This method will iterate on each received group, obtaining the fields array
   * for each group, and then processing the fields through the
   * {@link processGroupFields} method. Then, it will map the resolved fields
   * observable into a {@link GroupBase} object of {@link ResolvedTemplateField}.
   *
   * @see processGroupFields
   * @see ResolvedTemplateField
   */
  private processColumnGroups(
    groups: Array<GroupBase<MappedTemplateField>>
  ): Observable<Array<GroupBase<ResolvedTemplateField>>> {
    const groupsToProcess: Array<Observable<GroupBase<ResolvedTemplateField>>> = [];
    for (const group of groups) {
      const { fields } = group;
      const resolvedGroupFields$ = this.processGroupFields(fields);
      const mappedGroup$ = resolvedGroupFields$.pipe(
        map(groupFields => {
          return { ...group, fields: groupFields };
        })
      );
      groupsToProcess.push(mappedGroup$);
    }

    return forkJoin(groupsToProcess);
  }

  /**
   * Deeper level of mapping. This method takes each field value observable
   * and maps it into an object with all the original field attributes
   * plus the resolved value.
   *
   * @see ResolvedTemplateField
   */
  private processGroupFields(fields: MappedTemplateField[]): Observable<Array<ResolvedTemplateField>> {
    const valuesToProcess: Array<Observable<ResolvedTemplateField>> = [];
    for (const field of fields) {
      const mappedValue$ = field.value.pipe(
        map(value => {
          return { ...field, value } as ResolvedTemplateField;
        }),
        catchError(error => {
          this.logger.error(`Error processing field ${field.id}: ${error.message}`);
          return of({ ...field, value: '-' });
        })
      );
      valuesToProcess.push(mappedValue$);
    }

    /*
     * IMPORTANT NOTE: forkJoin([]) will never emit a value to the subscription.
     * An empty "fields" array ({ fields: [] }) in the blueprint JSON file made me
     * debug the whole process for around 5 hours.
     * To allow an empty array to work in the blueprint configuration, we
     * need to use the "defaultIfEmpty" operator.
     */
    return forkJoin(valuesToProcess).pipe(defaultIfEmpty([]));
  }
}
